import React from 'react';
import { Pressable, Text, StyleSheet, ViewStyle, TextStyle, ActivityIndicator, View, Platform } from 'react-native';
import { useStyles } from '../../hooks/useStyles';
import { createStyles } from '../../shared/utils/styles';
import { DesignTokens } from '../../shared/types/tokens';
import { Ionicons } from '@expo/vector-icons';

export interface ButtonProps {
  title: string;
  onPress: () => void;
  variant?: 'primary' | 'secondary' | 'danger' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  loading?: boolean;
  fullWidth?: boolean;
  leftIcon?: keyof typeof Ionicons.glyphMap;
  rightIcon?: keyof typeof Ionicons.glyphMap;
  style?: ViewStyle;
  textStyle?: TextStyle;
}

export function Button({
  title,
  onPress,
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  fullWidth = false,
  leftIcon,
  rightIcon,
  style,
  textStyle,
}: ButtonProps) {
  const { tokens, utils } = useStyles();
  const styles = makeStyles(tokens, utils);

  const variantStyle = styles[variant];
  const variantTextStyle = styles[`${variant}Text` as keyof typeof styles] as TextStyle;
  const sizeKey = `size${size.charAt(0).toUpperCase() + size.slice(1)}` as 'sizeSm' | 'sizeMd' | 'sizeLg';
  const sizeTextKey = `${sizeKey}Text` as 'sizeSmText' | 'sizeMdText' | 'sizeLgText';
  const sizeStyle = styles[sizeKey];
  const sizeTextStyle = styles[sizeTextKey] as TextStyle;
  const disabledStyle = (disabled || loading) ? styles.disabled : null;
  const fullWidthStyle = fullWidth ? styles.fullWidth : null;

  const iconColor = variant === 'outline' || variant === 'ghost' 
    ? tokens.color.onSurface 
    : variant === 'primary' 
      ? tokens.color.onPrimary 
      : variant === 'secondary'
        ? tokens.color.onSecondary
        : tokens.color.onError;

  const iconSize = size === 'sm' ? 16 : size === 'md' ? 18 : 20;

  return (
    <Pressable
      onPress={onPress}
      disabled={disabled || loading}
      style={({ pressed }) => [
        styles.base,
        variantStyle,
        sizeStyle,
        fullWidthStyle,
        disabledStyle,
        pressed && !disabled && !loading && styles.pressed,
        style,
      ]}
    >
      {loading ? (
        <ActivityIndicator 
          size="small" 
          color={iconColor} 
        />
      ) : (
        <View style={styles.content}>
          {leftIcon && (
            <Ionicons 
              name={leftIcon} 
              size={iconSize} 
              color={iconColor} 
              style={styles.leftIcon}
            />
          )}
          <Text style={[variantTextStyle, sizeTextStyle, textStyle]}>
            {title}
          </Text>
          {rightIcon && (
            <Ionicons 
              name={rightIcon} 
              size={iconSize} 
              color={iconColor} 
              style={styles.rightIcon}
            />
          )}
        </View>
      )}
    </Pressable>
  );
}

function makeStyles(tokens: DesignTokens, utils: ReturnType<typeof createStyles>) {
  return StyleSheet.create({
    base: {
      borderRadius: utils.radius.md,
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'row',
    },
    content: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
    },
    leftIcon: {
      marginRight: utils.spacing.xs,
    },
    rightIcon: {
      marginLeft: utils.spacing.xs,
    },
    // Sizes
    sizeSm: {
      paddingHorizontal: utils.spacing.md,
      paddingVertical: utils.spacing.sm,
      minHeight: 32,
    },
    sizeSmText: {
      fontSize: utils.typography.sizes.sm,
    },
    sizeMd: {
      paddingHorizontal: utils.spacing.lg,
      paddingVertical: utils.spacing.md,
      minHeight: 44,
    },
    sizeMdText: {
      fontSize: utils.typography.sizes.base,
    },
    sizeLg: {
      paddingHorizontal: utils.spacing.xl,
      paddingVertical: utils.spacing.lg,
      minHeight: 52,
    },
    sizeLgText: {
      fontSize: utils.typography.sizes.lg,
    },
    // Variants
    primary: {
      backgroundColor: tokens.color.primary,
    },
    primaryText: {
      color: tokens.color.onPrimary,
      fontWeight: utils.typography.weights.semibold,
      fontFamily: utils.typography.body.fontFamily,
    },
    secondary: {
      backgroundColor: tokens.color.secondary,
    },
    secondaryText: {
      color: tokens.color.onSecondary,
      fontWeight: utils.typography.weights.semibold,
      fontFamily: utils.typography.body.fontFamily,
    },
    danger: {
      backgroundColor: tokens.color.error,
    },
    dangerText: {
      color: tokens.color.onError,
      fontWeight: utils.typography.weights.semibold,
      fontFamily: utils.typography.body.fontFamily,
    },
    outline: {
      backgroundColor: 'transparent',
      borderWidth: 1,
      borderColor: tokens.color.border,
    },
    outlineText: {
      color: tokens.color.onSurface,
      fontWeight: utils.typography.weights.semibold,
      fontFamily: utils.typography.body.fontFamily,
    },
    ghost: {
      backgroundColor: 'transparent',
    },
    ghostText: {
      color: tokens.color.onSurface,
      fontWeight: utils.typography.weights.medium,
      fontFamily: utils.typography.body.fontFamily,
    },
    disabled: {
      opacity: 0.6,
    },
    pressed: {
      opacity: 0.8,
    },
    fullWidth: {
      width: '100%',
    },
  });
}
